import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { trpc } from "~/utils/trpc.client";
import { useSearchParamState } from "~/clientUtils";
import { Button } from "~/components/Button";
import Link from "~/components/Link";

export const Route = createFileRoute("/_authed/admin/rum-sessions")({
  component: RumSessions,
});

function RumSessions() {
  const [personId, setPersonId] = useSearchParamState("personId", "");
  const [teamId, setTeamId] = useSearchParamState("teamId", "");
  const [sortBy, setSortBy] = useSearchParamState<
    "created_at" | "last_event_at"
  >("sortBy", "last_event_at");

  const [queryOpts, setQueryOpts] = useState({
    personId: personId || undefined,
    teamId: teamId || undefined,
    sortBy,
  });

  const rumData = trpc.rum.getRumSessions.useQuery({
    ...queryOpts,
    limit: 100,
  });

  return (
    <div className="flex flex-col gap-4 m-4">
      <form
        className="grid grid-cols-[repeat(3,1fr)_max-content] gap-2 items-end"
        onSubmit={(e) => {
          e.preventDefault();
          setQueryOpts({
            personId: personId || undefined,
            teamId: teamId || undefined,
            sortBy,
          });
        }}
      >
        <div className="flex flex-col gap-1">
          <label className="text-xs opacity-70">Person ID</label>
          <input
            type="text"
            className="input input-bordered input-sm"
            value={personId}
            onChange={(e) => {
              setPersonId(e.target.value);
            }}
            placeholder="P..."
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs opacity-70">Team ID</label>
          <input
            type="text"
            className="input input-bordered input-sm"
            value={teamId}
            onChange={(e) => {
              setTeamId(e.target.value);
            }}
            placeholder="T..."
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs opacity-70">Sort By</label>
          <select
            className="select select-bordered select-sm"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as "created_at" | "last_event_at");
            }}
          >
            <option value="last_event_at">Last Activity</option>
            <option value="created_at">Created</option>
          </select>
        </div>
        <Button
          type="submit"
          loading={rumData.isFetching}
          className="btn-primary btn-sm btn h-[2.5rem]"
        >
          Search
        </Button>
      </form>

      {rumData.data ? (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Started</th>
                <th>Last Active</th>
                <th>Person</th>
                <th>Team</th>
                <th>Pages</th>
                <th>Duration</th>
                <th>Active</th>
                <th>Clicks</th>
                <th>Keys</th>
                <th>Sugs</th>
                <th>Opp Sugs</th>
                <th>Gen</th>
                <th>Dict</th>
              </tr>
            </thead>
            <tbody>
              {rumData.data.map((session) => (
                <tr key={session.id} className="hover">
                  <td className="whitespace-nowrap">
                    {session.createdAt.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap">
                    {session.lastEventAt.toLocaleString()}
                  </td>
                  <td>
                    <details className="text-sm marker:content-[''] marker:hidden cursor-pointer">
                      <summary>{session.personName}</summary>
                      <div className="text-xs opacity-70">
                        {session.personId}
                      </div>
                      <div className="text-xs opacity-70">
                        {session.personEmail}
                      </div>
                    </details>
                  </td>
                  <td>
                    <details className="text-sm marker:content-[''] marker:hidden cursor-pointer">
                      <summary>{session.teamName}</summary>
                      <div className="text-xs opacity-70">{session.teamId}</div>
                    </details>
                  </td>
                  <td>
                    <Link
                      href={`/admin/rum-page-views?sessionId="${session.id}"`}
                      className="link"
                    >
                      {session.pageViewCount} pages
                    </Link>
                  </td>
                  <td>{Math.round(session.totalDurationMs / 1000)}s</td>
                  <td>{Math.round(session.totalActiveDurationMs / 1000)}s</td>
                  <td>{session.totalClicks}</td>
                  <td>{session.totalKeypresses}</td>
                  <td>
                    {session.totalSuggestionsAccepted}/
                    {session.totalSuggestions}
                  </td>
                  <td>
                    {session.totalOpportunisticSuggestionsAccepted}/
                    {session.totalOpportunisticSuggestions}
                  </td>
                  <td>
                    {session.totalGenerationsAccepted}/
                    {session.totalGenerations}
                  </td>
                  <td>
                    {session.totalDictationsAccepted}/{session.totalDictations}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rumData.data.length === 0 && (
            <div className="flex h-16 items-center justify-center text-center text-sm italic opacity-70">
              No sessions found
            </div>
          )}
        </div>
      ) : (
        <div className="w-full flex justify-center">
          <div className="loading"></div>
        </div>
      )}
    </div>
  );
}
